import React, { useState, useEffect } from 'react';
import { Box, TextField, useTheme, Button, Snackbar, Alert, Typography, Checkbox, FormControlLabel, Grid, FormControl, Select, InputAdornment, IconButton } from '@mui/material';
import { saveCarrierAction, updateInsuranceCarrierAction, getCarrierAction } from '../../../redux/actions/agencyActions';
import { useDispatch } from 'react-redux';
import { stateList } from '../../../common/StaticFieldList';
import { useLocation, useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { groupStateList } from "../../../common/StaticFieldList";
import { convertToE164 } from '../../../common/helpers/commonFun';
import PhoneNumberField from '../../../common/PhoneNumberField';

const AddInsuranceCarrier = () => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const editUserDetail = location.state?.selectedUser;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formTouched, setFormTouched] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState({
    insuranceCarrier: '',
    carrierState: '',
    carrierProgramDescription: '',
    agencyUserName: '',
    agencyPassword: '',
    confirmPassword: '',
    selectLogin: '',
    producerCode: '',
    subCode: '',
    ratingUserName: '',
    ratingPassword: '',
    agencyName: '',
    accountNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    fax: '',
    website: '',
    email: '',
    alternatePhone: '',
    enableTwoFactorAuthentication: false,
    singleSignOnURL: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editUserDetail) {
      setFormData({
        ...editUserDetail,
        agencyPassword: '',
        confirmPassword: '',
      });
    }
  }, [editUserDetail]);

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{8,})/;

    switch (name) {
      case 'insuranceCarrier':
        if (!value || !value.trim()) {
          newErrors.insuranceCarrier = 'Insurance Company Name is required.';
        } else if (value.length < 2) {
          newErrors.insuranceCarrier = 'Insurance Company Name must be at least 2 characters.';
        } else {
          delete newErrors.insuranceCarrier;
        }
        break;

      case 'carrierState':
        if (!value || value === '') {
          newErrors.carrierState = 'Carrier State is required.';
        } else {
          delete newErrors.carrierState;
        }
        break;

      case 'carrierProgramDescription':
        if (!value || !value.trim()) {
          newErrors.carrierProgramDescription = 'Company Program Description is required.';
        } else if (value.length < 2) {
          newErrors.carrierProgramDescription = 'Description must be at least 2 characters.';
        } else {
          delete newErrors.carrierProgramDescription;
        }
        break;

      case 'agencyUserName':
        if (!value || !value.trim()) {
          newErrors.agencyUserName = 'User Name is required.';
        } else {
          const isEmailFormat = value.includes('@') || value.includes('.com') || value.includes('.co') || value.includes('.in');
          if (isEmailFormat && !emailRegex.test(value)) {
            newErrors.agencyUserName = 'Please enter a valid email address.';
          } else {
            delete newErrors.agencyUserName;
          }
        }
        break;

      case 'agencyPassword':
        if (!editUserDetail) {
          if (!value || !value.trim()) {
            newErrors.agencyPassword = 'Password is required.';
          } else if (!passwordRegex.test(value)) {
            newErrors.agencyPassword = 'Password must be at least 8 characters, include one uppercase letter and one special character.';
          } else {
            delete newErrors.agencyPassword;
          }
        }
        break;

      case 'confirmPassword':
        if (!editUserDetail) {
          if (!value || !value.trim()) {
            newErrors.confirmPassword = 'Confirm Password is required.';
          } else if (value !== formData.agencyPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
          } else {
            delete newErrors.confirmPassword;
          }
        }
        break;

      case 'agencyName':
        if (!value || !value.trim()) {
          newErrors.agencyName = 'Agency Name is required.';
        } else if (value.length < 2) {
          newErrors.agencyName = 'Agency Name must be at least 2 characters.';
        } else {
          delete newErrors.agencyName;
        }
        break;

      case 'state':
        if (!value || value === '') {
          newErrors.state = 'State is required.';
        } else {
          delete newErrors.state;
        }
        break;

      case 'email':
        if (!value || !value.trim()) {
          newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Please enter a valid email address.';
        } else {
          delete newErrors.email;
        }
        break;

      case 'phone':
        if (!value || !value.trim()) {
          newErrors.phone = 'Phone is required.';
        } else if (value.replace(/\D/g, '').length < 10) {
          newErrors.phone = 'Phone number must be at least 10 digits.';
        } else {
          delete newErrors.phone;
        }
        break;

      case 'fax':
        if (value && value.replace(/\D/g, '').length > 0 && value.replace(/\D/g, '').length < 10) {
          newErrors.fax = 'Fax number must be at least 10 digits.';
        } else {
          delete newErrors.fax;
        }
        break;

      case 'alternatePhone':
        if (value && value.replace(/\D/g, '').length > 0 && value.replace(/\D/g, '').length < 10) {
          newErrors.alternatePhone = 'Alternate phone number must be at least 10 digits.';
        } else {
          delete newErrors.alternatePhone;
        }
        break;

      case 'zipCode':
        if (!value || !value.trim()) {
          newErrors.zipCode = 'Zip Code is required.';
        } else if (value.length < 5) {
          newErrors.zipCode = 'Zip code must be 5 digits.';
        } else {
          delete newErrors.zipCode;
        }
        break;

      case 'city':
        if (!value || !value.trim()) {
          newErrors.city = 'City is required.';
        } else if (value.length < 2) {
          newErrors.city = 'City must be at least 2 characters.';
        } else {
          delete newErrors.city;
        }
        break;

      case 'address1':
        if (!value || !value.trim()) {
          newErrors.address1 = 'Address Line 1 is required.';
        } else if (value.length < 5) {
          newErrors.address1 = 'Address must be at least 5 characters.';
        } else {
          delete newErrors.address1;
        }
        break;

      case 'website':
        if (value && value.trim() && !value.match(/^https?:\/\/.+\..+/)) {
          newErrors.website = 'Please enter a valid URL (e.g., https://example.com).';
        } else {
          delete newErrors.website;
        }
        break;

      case 'accountNumber':
        if (!value || !value.trim()) {
          newErrors.accountNumber = 'Account Number is required.';
        } else if (value.length < 3) {
          newErrors.accountNumber = 'Account Number must be at least 3 digits.';
        } else {
          delete newErrors.accountNumber;
        }
        break;

      case 'producerCode':
        if (!value || !value.trim()) {
          newErrors.producerCode = 'Producer Code is required.';
        } else if (value.length < 2) {
          newErrors.producerCode = 'Producer Code must be at least 2 characters.';
        } else {
          delete newErrors.producerCode;
        }
        break;

      case 'subCode':
        if (!value || !value.trim()) {
          newErrors.subCode = 'Sub Code is required.';
        } else {
          delete newErrors.subCode;
        }
        break;

      default:
        break;
    }

    return newErrors;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFormTouched(prev => ({ ...prev, [name]: true }));
    const newErrors = validateField(name, value);
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const formattedValue = name === 'phone' || name === 'Phonealternate' || name === 'fax' ? formatPhoneNumber(value) : value;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : formattedValue,
    });


    if (formTouched[name]) {
      const newErrors = validateField(name, type === 'checkbox' ? checked : formattedValue);
      setErrors(newErrors);
    }


    if (name === 'agencyPassword' && formTouched.confirmPassword) {
      const newErrors = validateField('confirmPassword', formData.confirmPassword);
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    let newErrors = {};
    const requiredFields = [
      'insuranceCarrier', 'carrierState', 'carrierProgramDescription',
      'agencyUserName', 'agencyName', 'state', 'email', 'phone',
      'zipCode', 'city', 'address1', 'accountNumber', 'producerCode', 'subCode'
    ];

    if (!editUserDetail) {
      requiredFields.push('agencyPassword', 'confirmPassword');
    }


    Object.keys(formData).forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      newErrors = { ...newErrors, ...fieldErrors };
    });


    const allTouched = {};
    Object.keys(formData).forEach(field => {
      allTouched[field] = true;
    });
    setFormTouched(allTouched);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      const formatted = [
        match[1],
        match[2],
        match[3],
      ]
        .filter(Boolean)
        .join("-");
      return formatted;
    }
    return value;
  };

 const handleSubmit = (e) => {
  e.preventDefault();
  if (validateForm()) {
    const processedFormData = {
      ...formData,
      alternatePhone: formData.alternatePhone && formData.alternatePhone.trim()
        ? convertToE164(formData.alternatePhone)
        : null,
      fax: formData.fax && formData.fax.trim()
        ? convertToE164(formData.fax)
        : null,
      phone: convertToE164(formData.phone),
      website: formData.website && formData.website.trim() ? formData.website : null,
      address2: formData.address2 && formData.address2.trim() ? formData.address2 : null,
      selectLogin: formData.selectLogin && formData.selectLogin.trim() ? formData.selectLogin : null,
      ratingUserName: formData.ratingUserName && formData.ratingUserName.trim() ? formData.ratingUserName : null,
      ratingPassword: formData.ratingPassword && formData.ratingPassword.trim() ? formData.ratingPassword : null,
      singleSignOnURL: formData.singleSignOnURL && formData.singleSignOnURL.trim() ? formData.singleSignOnURL : null,
    };

    if (editUserDetail) {
      const updatedFormValues = {
        ...processedFormData,
        agencyId: editUserDetail.agencyId,
      };

      dispatch(updateInsuranceCarrierAction(updatedFormValues, (result) => {
        if (result.success === true) {
          setSnackbar({
            open: true,
            message: 'Carrier Group updated successfully',
            severity: 'success',
          });
          setTimeout(() => {
            navigate('/managecarrier');
            dispatch(getCarrierAction());
          }, 2000);
        } else {
          // Extract specific error message from backend
          let errorMessage = 'Error updating Carrier Group. Please try again.';

          if (result.error?.errors && Array.isArray(result.error.errors) && result.error.errors.length > 0) {
            errorMessage = result.error.errors[0].errorMessage;
          } else if (result.error?.message) {
            errorMessage = result.error.message;
          }

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
      }));
    } else {
      dispatch(saveCarrierAction(processedFormData, (result) => {
        if (result.success === true) {
          setSnackbar({
            open: true,
            message: 'Carrier Group created successfully',
            severity: 'success',
          });
          setTimeout(() => {
            navigate('/managecarrier');
          }, 2000);
        } else {
          // Extract specific error message from backend
          let errorMessage = 'Error creating Carrier Group. Please try again.';

          if (result.error?.errors && Array.isArray(result.error.errors) && result.error.errors.length > 0) {
            errorMessage = result.error.errors[0].errorMessage;
          } else if (result.error?.message) {
            errorMessage = result.error.message;
          }

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
      }));
    }
  }
};

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const cancelAddInsuranceCarrier = () => {
    navigate('/managecarrier');
  };

  const labelStyle = {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "19.6px",
    textAlign: "left",
    marginBottom: "8px",
  };

  const RequiredLabel = ({ children, required = false }) => (
    <Typography variant="body1" sx={labelStyle}>
      {children}
      {required && <span style={{ color: theme.palette.error.main, marginLeft: '2px' }}>*</span>}
    </Typography>
  );

  return (
    <Box sx={{
      padding: '20px',
      margin: '20px',
      border: '1px solid rgba(57, 57, 57, 0.1)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>

      <Typography variant="h2" sx={{ marginBottom: '20px',}}>
        {editUserDetail ? 'Edit Insurance Carrier' : 'Add Insurance Carrier'}
      </Typography>

      <div style={{ paddingBottom: '20px' }}>
        <hr style={{
          borderTop: "1px solid #ccc",
          width: "100%",
          margin: "0",
        }} />
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <RequiredLabel required>Insurance Company Name</RequiredLabel>
            <TextField
              id="insuranceCarrier"
              name="insuranceCarrier"
              fullWidth
              value={formData.insuranceCarrier}
              onChange={(e) => {
                const alphanumericValue = e.target.value.replace(/[^a-z0-9\s&']/gi, '');
                handleChange({ target: { name: "insuranceCarrier", value: alphanumericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.insuranceCarrier && formTouched.insuranceCarrier)}
              helperText={formTouched.insuranceCarrier ? errors.insuranceCarrier : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Carrier State</RequiredLabel>
            <Select
              native
              id="carrierState"
              name="carrierState"
              fullWidth
              value={formData.carrierState}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.carrierState && formTouched.carrierState)}
              inputProps={{
                id: 'carrierState-native',
              }}
            >
              <option value="">Select a carrier state</option>
              {groupStateList.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </Select>
            {formTouched.carrierState && errors.carrierState && (
              <Typography variant="caption" sx={{ color: theme.palette.error.main, marginLeft: '14px', fontSize: '0.75rem' }}>
                {errors.carrierState}
              </Typography>
            )}
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Company Program Description</RequiredLabel>
            <TextField
              id="programDescription"
              name="carrierProgramDescription"
              fullWidth
              value={formData.carrierProgramDescription}
              onChange={(e) => {
                const alphanumericValue = e.target.value.replace(/[^a-z0-9\s]/gi, '');
                handleChange({ target: { name: "carrierProgramDescription", value: alphanumericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.carrierProgramDescription && formTouched.carrierProgramDescription)}
              helperText={formTouched.carrierProgramDescription ? errors.carrierProgramDescription : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>User Name</RequiredLabel>
            <TextField
              id="userName"
              name="agencyUserName"
              fullWidth
              value={formData.agencyUserName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.agencyUserName && formTouched.agencyUserName)}
              helperText={formTouched.agencyUserName ? errors.agencyUserName : ''}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required={!editUserDetail}>Password</RequiredLabel>
            <TextField
              id="password"
              name="agencyPassword"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={formData.agencyPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.agencyPassword && formTouched.agencyPassword)}
              helperText={formTouched.agencyPassword ? errors.agencyPassword : ''}
              inputProps={{ maxLength: 30 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handlePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required={!editUserDetail}>Confirm Password</RequiredLabel>
            <TextField
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.confirmPassword && formTouched.confirmPassword)}
              helperText={formTouched.confirmPassword ? errors.confirmPassword : ''}
              inputProps={{ maxLength: 30 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Select Login</RequiredLabel>
            <TextField
              id="selectLogin"
              name="selectLogin"
              type="text"
              fullWidth
              value={formData.selectLogin}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.selectLogin && formTouched.selectLogin)}
              helperText={formTouched.selectLogin ? errors.selectLogin : ''}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Producer Code</RequiredLabel>
            <TextField
              id="producerCode"
              name="producerCode"
              fullWidth
              value={formData.producerCode}
              onChange={(e) => {
                const alphanumericValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                handleChange({ target: { name: "producerCode", value: alphanumericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.producerCode && formTouched.producerCode)}
              helperText={formTouched.producerCode ? errors.producerCode : ''}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>

         <Grid item xs={3}>
          <RequiredLabel required>Sub Code</RequiredLabel>
          <TextField
            id="subCode"
            name="subCode"
            fullWidth
            value={formData.subCode}
            onChange={(e) => {

              handleChange({ target: { name: "subCode", value: e.target.value } });
            }}
            onBlur={handleBlur}
            error={!!(errors.subCode && formTouched.subCode)}
            helperText={formTouched.subCode ? errors.subCode : ''}
            inputProps={{ maxLength: 10 }}
          />
        </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Rating User Name</RequiredLabel>
            <TextField
              id="ratingUserName"
              name="ratingUserName"
              fullWidth
              value={formData.ratingUserName}
              onChange={(e) => {
                const alphanumericValue = e.target.value.replace(/[^a-z0-9\s]/gi, '');
                handleChange({ target: { name: "ratingUserName", value: alphanumericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.ratingUserName && formTouched.ratingUserName)}
              helperText={formTouched.ratingUserName ? errors.ratingUserName : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Rating Password</RequiredLabel>
            <TextField
              id="ratingPassword"
              name="ratingPassword"
              type="password"
              fullWidth
              value={formData.ratingPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.ratingPassword && formTouched.ratingPassword)}
              helperText={formTouched.ratingPassword ? errors.ratingPassword : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Agency Name</RequiredLabel>
            <TextField
              id="agencyName"
              name="agencyName"
              fullWidth
              value={formData.agencyName}
              onChange={(e) => {
                const alphanumericValue = e.target.value.replace(/[^a-z0-9\s]/gi, '');
                handleChange({ target: { name: "agencyName", value: alphanumericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.agencyName && formTouched.agencyName)}
              helperText={formTouched.agencyName ? errors.agencyName : ''}
              inputProps={{ maxLength: 70 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Account Number</RequiredLabel>
            <TextField
              id="accountNumber"
              name="accountNumber"
              fullWidth
              value={formData.accountNumber}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, "");
                handleChange({ target: { name: "accountNumber", value: numericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.accountNumber && formTouched.accountNumber)}
              helperText={formTouched.accountNumber ? errors.accountNumber : "Enter up to 20 numeric digits"}
              inputProps={{ maxLength: 20 }}
              placeholder="Numbers only (max 20 digits)"
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Address Line 1</RequiredLabel>
            <TextField
              id="address1"
              name="address1"
              fullWidth
              value={formData.address1}
              onChange={(e) => {

                const allowedValue = e.target.value.replace(/[^a-zA-Z0-9\s,\-.'#]/g, '');
                handleChange({ target: { name: "address1", value: allowedValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.address1 && formTouched.address1)}
              helperText={formTouched.address1 ? errors.address1 : ''}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Address Line 2</RequiredLabel>
            <TextField
              id="address2"
              name="address2"
              fullWidth
              value={formData.address2}
              onChange={(e) => {

                const allowedValue = e.target.value.replace(/[^a-zA-Z0-9\s,\-.'#]/g, '');
                handleChange({ target: { name: "address2", value: allowedValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.address2 && formTouched.address2)}
              helperText={formTouched.address2 ? errors.address2 : ''}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>City</RequiredLabel>
            <TextField
              id="city"
              name="city"
              fullWidth
              value={formData.city}
              onChange={(e) => {

                const allowedValue = e.target.value.replace(/[^a-zA-Z\s\-.']/g, '');
                handleChange({ target: { name: "city", value: allowedValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.city && formTouched.city)}
              helperText={formTouched.city ? errors.city : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth>
              <RequiredLabel required>State</RequiredLabel>
              <Select
                native
                name="state"
                value={formData.state}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!(errors.state && formTouched.state)}
                inputProps={{
                  id: 'state-native',
                }}
              >
                <option value="">Select State</option>
                {stateList.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
              {formTouched.state && errors.state && (
                <Typography variant="caption" sx={{ color: theme.palette.error.main, marginLeft: '14px', fontSize: '0.75rem' }}>
                  {errors.state}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Zip Code</RequiredLabel>
            <TextField
              id="zipCode"
              name="zipCode"
              fullWidth
              value={formData.zipCode}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, "");
                handleChange({ target: { name: "zipCode", value: numericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.zipCode && formTouched.zipCode)}
              helperText={formTouched.zipCode ? errors.zipCode : ''}
              inputProps={{ maxLength: 5 }}
            />
          </Grid>

         <Grid item xs={3}>
            {/* <RequiredLabel required>Phone</RequiredLabel> */}
           <PhoneNumberField
            id="phone"
            name="phone"
            fullWidth
            value={formData.phone}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              handleChange({ target: { name: "phone", value: numericValue } });
            }}
            onBlur={handleBlur}
            error={!!(errors.phone && formTouched.phone)}
            helperText={formTouched.phone ? errors.phone : ''}
            inputProps={{ maxLength: 12 }}
            labelStyle={{ fontSize: "14px", fontWeight: 500 ,mb:1 }}
          />

          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Fax</RequiredLabel>
            <TextField
              id="fax"
              name="fax"
              fullWidth
              value={formData.fax}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/\D/g, "");
                handleChange({ target: { name: "fax", value: numericValue } });
              }}
              onBlur={handleBlur}
              error={!!(errors.fax && formTouched.fax)}
              helperText={formTouched.fax ? errors.fax : ''}
              inputProps={{ maxLength: 12 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Website</RequiredLabel>
            <TextField
              id="website"
              name="website"
              fullWidth
              value={formData.website}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.website && formTouched.website)}
              helperText={formTouched.website ? errors.website : ''}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel required>Email</RequiredLabel>
            <TextField
              id="email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.email && formTouched.email)}
              helperText={formTouched.email ? errors.email : ''}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={3}>

            <PhoneNumberField
              id="alternatePhone"
              name="alternatePhone"
              fullWidth
              value={formData.alternatePhone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.alternatePhone && formTouched.alternatePhone)}
              helperText={formTouched.alternatePhone ? errors.alternatePhone : ''}
              inputProps={{ maxLength: 12 }}
              label="Alternate Phone"
              required={false}
              labelStyle={{ fontSize: 14, fontWeight: 500,mb:1}}
            />
          </Grid>

          <Grid item xs={3}>
            <RequiredLabel>Single Sign-On URL</RequiredLabel>
            <TextField
              id="singleSignOnUrl"
              name="singleSignOnURL"
              fullWidth
              value={formData.singleSignOnURL}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!(errors.singleSignOnURL && formTouched.singleSignOnURL)}
              helperText={formTouched.singleSignOnURL ? errors.singleSignOnURL : ''}
              inputProps={{ maxLength: 30 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  id="twoFactorAuth"
                  name="enableTwoFactorAuthentication"
                  checked={formData.enableTwoFactorAuthentication}
                  onChange={handleChange}
                />
              }
              label="Enable Two-Factor Authentication"
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={cancelAddInsuranceCarrier} id="cancelButton" sx={{mr:2}}>
              Cancel
            </Button>

            <Button variant="contained"type="submit" id="submitButton">
              {editUserDetail ? 'Update' : 'Save'}
            </Button>

          </Grid>
        </Grid>
      </form>

        <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddInsuranceCarrier;